// Watermark truth for the WHOLE set of listings in a contact's minisite, so the
// Matches tab can show an at-a-glance "N of M listings are clean" and drive a bulk
// removal that skips whatever is already done.
//
// MARKET vs OWN — the split every consumer here reads (customer report 2026-08-11:
// "we propose to remove watermarks from all listings, but that isn't really needed on
// listings that aren't from the שוק"). A scraped yad2/madlan photo carries someone
// else's watermark, which is the entire point of the feature; the realtor's own (and
// their agency's) photos are already theirs, so removal there is an ODD case, not the
// expected one. Both groups stay fully actionable — the realtor can still run removal
// on their own listings — but only the market group is counted in the header status
// and pre-selected in the bulk dialog. Never collapse the two back into one number:
// that is exactly what made a minisite of the realtor's own listings report
// "0 מתוך 5 נכסים נקיים" and nag for a removal nobody needed.
//
// SHARED, not per-user (changed 2026-09-30). The stored clean versions are a cross-user
// cache on the listing doc, and a listing counts as clean here whenever that cache holds
// its images — whoever generated them. This used to require that THE SIGNED-IN USER had
// personally run the removal, which made a fully-cleaned listing report
// "0 מתוך 12 תמונות נקיות" and offer to regenerate twelve images that already existed.
// The only gate left is the realtor's rights acceptance (User.watermarkTermsAcceptedAt),
// which is per realtor and forever, so it has no place in a per-listing status.
//
// This is the multi-listing sibling of components/_dashboard/watermarks/
// useListingWatermarkInfo (which is one-listing-only and can't be called in a loop —
// rules of hooks). It reuses that feature's OWN definition of "clean",
// buildWatermarkFreeUrlMap, so this tab can never disagree with the removal studio, the
// per-listing chip or ListingEditDialog about whether an image is cleaned.
//
// Two sources, layered:
//  1. SEED — the MatchHit objects the tab already holds. Favorite listings are read
//     from Firestore once (matchesData.reconcileFavoriteListingsData → fetchListingsByIds)
//     and cached, so they carry `imageUrls` + `watermarkFreeImages` and the status paints
//     instantly with zero extra reads.
//  2. LIVE — one onSnapshot per listing doc. Needed because the seed is a point-in-time
//     read: after a bulk run (or a colleague cleaning the same listing in parallel) it
//     would otherwise stay stale and lie. The CARDS' displayed photos also ride this
//     live source (via `watermarkFreeImages` + `removedByUser` on each status) — the
//     cached seed must never decide what the thumbs show, or a just-finished removal
//     keeps rendering watermarked thumbs until a full reload.
//
// The seed can only ever UNDER-report cleanliness (search hits may not carry
// watermarkFreeImages at all), never over-report it — so the honest direction is the
// only one it can drift in, and the listener corrects it within milliseconds.
import { useEffect, useMemo, useState } from "react";
import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { buildWatermarkFreeUrlMap } from "utils/listings/watermarkFree";
import {
  PROCESSING_LISTINGS_COLLECTION_NAME
} from "../../../../../../@types/sharedSchema";
import type { ProcessedListingData } from "../../../../../../@types/sharedSchema";
import type { MatchHit } from "./matchesSearch";
import { buildListingAddressLine } from "./matchesDisplay";

/** Safety valve: a contact with a huge minisite must not open hundreds of listeners. */
const MAX_LIVE_LISTENERS = 60;

export type ListingWatermarkStatus = {
  listingId: string;
  /** Address line, for the bulk dialog's per-listing rows. */
  label: string;
  totalCount: number;
  /** Images the SIGNED-IN USER sees as clean (0 until they performed a removal). */
  cleanedCount: number;
  /**
   * Images that genuinely have no clean version yet, i.e. what a removal run would actually
   * generate. Empty on a listing someone else already cleaned — the clean files exist and this
   * realtor renders them, so there is nothing to ask for and nothing to pay for.
   */
  pendingUrls: string[];
  /**
   * Live-or-seed stored clean versions — the shared cross-user cache as-is. Exposed so cards render
   * from the LIVE doc rather than the stale cached seed. Display no longer needs to be paired with
   * a per-listing flag; the only gate is the realtor's `watermarkTermsAcceptedAt`.
   */
  watermarkFreeImages: ProcessedListingData["watermarkFreeImages"];
  /**
   * The listing has a marketplace doc with at least one image, i.e. there is something
   * watermark-removal could act on. Webot-only listings (no yad2-listings-latest doc)
   * and image-less listings are false and are excluded from every count.
   */
  tracked: boolean;
  /**
   * Scraped from the MARKET (yad2 / madlan) rather than being the realtor's own or
   * their agency's (webot origin). This is the same origin test `segmentMatchHits`
   * uses to split שלי / משרד / מהשוק, and it survives "ייבא והוסף": adding a market
   * listing to a minisite stores its id, it never rewrites `listingOrigin`.
   */
  isMarketListing: boolean;
};

/** Aggregate for one side of the market/own split. */
export type WatermarkGroupSummary = {
  /** Tracked listings in this group. */
  statuses: ListingWatermarkStatus[];
  /** …of those, the ones with at least one image this user hasn't cleaned. */
  pendingStatuses: ListingWatermarkStatus[];
  trackedCount: number;
  /** Tracked listings in this group that are fully clean FOR THIS USER. */
  cleanCount: number;
  /** Total un-cleaned (for this user) images across `pendingStatuses`. */
  pendingImageCount: number;
  /** Every tracked listing in this group is clean (false when the group is empty). */
  allClean: boolean;
};

export type MatchesWatermarkStatus = {
  /** Every listing passed in, in the same order. */
  statuses: ListingWatermarkStatus[];
  /** Only the ones watermark removal can act on. */
  trackedStatuses: ListingWatermarkStatus[];
  /** yad2 / madlan — what the header status reports and the bulk dialog pre-selects. */
  market: WatermarkGroupSummary;
  /** The realtor's own + their agency's listings — offered, never assumed. */
  own: WatermarkGroupSummary;
};

type LiveDoc = Pick<
  ProcessedListingData,
  "imageUrls" | "watermarkFreeImages" | "watermarkRemovalRequestedBy"
> | null;

/**
 * @param listings the contact's minisite listings (MatchHit = ProcessedListingData & { objectID })
 * @param enabled  attach the live listeners. Pass false while the surface showing this
 *                 status is hidden — the seed still renders, it just stops updating.
 */
export function useMatchesWatermarkStatus(
  listings: MatchHit[] | undefined,
  enabled: boolean
): MatchesWatermarkStatus {
  const [liveDocs, setLiveDocs] = useState<Map<string, LiveDoc>>(new Map());

  const items = useMemo(() => listings ?? [], [listings]);
  // Stable primitive dep — `items` is a fresh array on most renders.
  const idsKey = items.map((listing) => listing.objectID).join(",");

  useEffect(() => {
    if (!enabled || !idsKey) return undefined;
    const ids = idsKey.split(",").slice(0, MAX_LIVE_LISTENERS);
    const unsubscribes = ids.map((id) =>
      db
        .collection(PROCESSING_LISTINGS_COLLECTION_NAME)
        .doc(id)
        .withConverter(GenericConverter<ProcessedListingData>())
        .onSnapshot(
          (snapshot) => {
            setLiveDocs((prev) => {
              const next = new Map(prev);
              next.set(id, snapshot.data() ?? null);
              return next;
            });
          },
          (e) => console.error(`useMatchesWatermarkStatus - snapshot error for ${id}`, e)
        )
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [idsKey, enabled]);

  return useMemo(() => {
    const statuses = items.map((listing) => {
      const listingId = listing.objectID;
      const live = liveDocs.get(listingId);
      // Live doc wins the moment it lands; until then the seed keeps the UI honest-ish.
      const source: Pick<ProcessedListingData, "imageUrls" | "watermarkFreeImages"> = live ?? listing;
      const imageUrls = source.imageUrls ?? [];
      /*
       * The SHARED cache, always — no per-user emptying (2026-09-30).
       *
       * This map used to be emptied for anyone who had not personally run the removal, which made
       * every image count as "pending" and the chip report "0 מתוך 12 תמונות נקיות" on a listing
       * the system already held twelve clean copies of — then offer to regenerate all twelve.
       * There is no per-listing gate any more; the only gate is the realtor's rights acceptance,
       * and it is per realtor, not per listing.
       */
      const cleanBySource = buildWatermarkFreeUrlMap(source);
      // Filter FROM imageUrls, never count watermarkFreeImages.length: a re-scraped
      // listing keeps clean entries for URLs that are no longer part of the listing.
      const pendingUrls = imageUrls.filter((url) => !cleanBySource.has(url));
      // `undefined` = the listener hasn't answered yet (assume the doc exists);
      // `null` = it answered and the doc is genuinely missing.
      const hasMarketplaceDoc = liveDocs.has(listingId) ? live !== null : true;
      return {
        listingId,
        label: buildListingAddressLine(listing),
        totalCount: imageUrls.length,
        cleanedCount: imageUrls.length - pendingUrls.length,
        pendingUrls,
        watermarkFreeImages: source.watermarkFreeImages,
        tracked: hasMarketplaceDoc && imageUrls.length > 0,
        // Read off the SEED, not the live doc: `listingOrigin` is immutable for a
        // document (it records where the listing came from), and the live Pick above
        // deliberately fetches only the watermark fields.
        isMarketListing: listing.listingOrigin !== "webot"
      };
    });

    const trackedStatuses = statuses.filter((status) => status.tracked);
    const summarize = (groupStatuses: ListingWatermarkStatus[]): WatermarkGroupSummary => {
      const pendingStatuses = groupStatuses.filter((status) => status.pendingUrls.length > 0);
      return {
        statuses: groupStatuses,
        pendingStatuses,
        trackedCount: groupStatuses.length,
        cleanCount: groupStatuses.length - pendingStatuses.length,
        pendingImageCount: pendingStatuses.reduce(
          (total, status) => total + status.pendingUrls.length,
          0
        ),
        allClean: groupStatuses.length > 0 && pendingStatuses.length === 0
      };
    };

    return {
      statuses,
      trackedStatuses,
      market: summarize(trackedStatuses.filter((status) => status.isMarketListing)),
      own: summarize(trackedStatuses.filter((status) => !status.isMarketListing))
    };
  }, [items, liveDocs]);
}
